import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/AppLayout";
import { CardGridSkeleton, StatCardsSkeleton } from "@/components/PageSkeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/DatePicker";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { useToast } from "@/hooks/use-toast";
import {
  apiGetExpenses, apiApproveExpense, apiRejectExpense, apiReimburseExpense,
  apiGetExpense, apiGetExpenseAnalytics, getExpenseReceiptUrl, apiRefreshFileToken,
  apiGetCashAdvances, apiApproveCashAdvanceLM, apiRejectCashAdvanceLM,
  apiApproveCashAdvanceHR, apiRejectCashAdvanceHR,
  apiApproveCashAdvanceReport, apiRejectCashAdvanceReport,
  apiSettleCashAdvance, apiGetCashAdvance, getCashAdvanceReceiptUrl,
  apiGetDepartments, apiGetTeamNames,
  type Expense, type CashAdvance,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ReceiptText, CheckCircle2, XCircle, Banknote, Clock, FileText,
  AlertCircle, Filter, Download, TrendingUp, DollarSign, Layers,
  Wallet, Users, X,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { format } from "date-fns";

// ─── Constants ────────────────────────────────────────────────────────────────

const CHART_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#3b82f6", "#ec4899", "#14b8a6"];

const CATEGORIES = [
  { value: "travel",        label: "Travel" },
  { value: "food",          label: "Food & Meals" },
  { value: "office",        label: "Office Supplies" },
  { value: "accommodation", label: "Accommodation" },
  { value: "software",      label: "Software & Subscriptions" },
  { value: "other",         label: "Other" },
];

const STATUS_VARIANTS: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ElementType }> = {
  draft:            { variant: "secondary",   icon: FileText },
  submitted:        { variant: "outline",     icon: Clock },
  pending_approval: { variant: "outline",     icon: Clock },
  approved:         { variant: "default",     icon: CheckCircle2 },
  rejected:         { variant: "destructive", icon: XCircle },
  reimbursed:       { variant: "default",     icon: Banknote },
};

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const cfg = STATUS_VARIANTS[status] ?? { variant: "outline" as const, icon: AlertCircle };
  const Icon = cfg.icon;
  const labelMap: Record<string, string> = {
    draft: t("expenses.status.draft"),
    submitted: t("expenses.status.submitted"),
    pending_approval: t("expenses.status.pending"),
    approved: t("expenses.status.approved"),
    rejected: t("expenses.status.rejected"),
    reimbursed: t("expenses.status.reimbursed"),
  };
  return (
    <Badge variant={cfg.variant} className="gap-1 text-xs">
      <Icon className="h-3 w-3" />
      {labelMap[status] ?? status}
    </Badge>
  );
}

// ════════════════════════════════════════════════════════════════════
// Main page
// ════════════════════════════════════════════════════════════════════

// ─── Cash Advance status config ──────────────────────────────────────────────

const CA_STATUS_COLORS: Record<string, { color: string; icon: React.ElementType }> = {
  pending_lm:        { color: "#f59e0b", icon: Clock },
  pending_hr:        { color: "#f59e0b", icon: Clock },
  approved:          { color: "#22c55e", icon: CheckCircle2 },
  report_submitted:  { color: "#3b82f6", icon: FileText },
  report_pending_hr: { color: "#6366f1", icon: Banknote },
  report_rejected:   { color: "#ef4444", icon: XCircle },
  settled:           { color: "#8b5cf6", icon: Banknote },
  rejected:          { color: "#ef4444", icon: XCircle },
};

function CaStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const cfg = CA_STATUS_COLORS[status] ?? { color: "#94a3b8", icon: AlertCircle };
  const caLabelMap: Record<string, string> = {
    pending_lm: t("expenses.caStatus.awaitingManager"),
    pending_hr: t("expenses.caStatus.awaitingHr"),
    approved: t("expenses.caStatus.awaitingReport"),
    report_submitted: t("expenses.caStatus.reportSubmitted"),
    report_pending_hr: t("expenses.caStatus.readyForSettlement"),
    report_rejected: t("expenses.caStatus.reportRejected"),
    settled: t("expenses.caStatus.settled"),
    rejected: t("expenses.caStatus.rejected"),
  };
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border"
      style={{ color: cfg.color, borderColor: cfg.color + "40", backgroundColor: cfg.color + "18" }}
    >
      <Icon className="h-3 w-3" />
      {caLabelMap[status] ?? status}
    </span>
  );
}

export default function Expenses() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Keep a short-lived file-access token fresh for loading private receipt files.
  useEffect(() => {
    apiRefreshFileToken();
    const id = setInterval(apiRefreshFileToken, 8 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  const { user } = useAuth();

  const [tab, setTab] = useState("overview");
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [rejectDialog, setRejectDialog] = useState<Expense | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [reimburseDialog, setReimburseDialog] = useState<Expense | null>(null);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);

  // Filters for All Expenses tab
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  // Cash advance state
  const [caStatusFilter, setCaStatusFilter] = useState("all");
  const [caApproveDialog, setCaApproveDialog] = useState<CashAdvance | null>(null);
  const [caApproveType, setCaApproveType] = useState<"lm" | "hr" | "report_lm" | "settle">("lm");
  const [caRejectDialog, setCaRejectDialog] = useState<CashAdvance | null>(null);
  const [caRejectType, setCaRejectType] = useState<"lm" | "hr" | "report_lm">("lm");
  const [caRejectReason, setCaRejectReason] = useState("");
  const [caSettleNotes, setCaSettleNotes] = useState("");
  const [caDetailOpen, setCaDetailOpen] = useState(false);
  const [caDetailAdv, setCaDetailAdv] = useState<CashAdvance | null>(null);

  const userRole = user?.role?.mainRole;
  const isHrAdmin = userRole === "hr" || userRole === "admin";
  const isLM = userRole !== "hr" && userRole !== "admin" &&
    user?.role?.subRole === "line_manager";

  // Analytics date range + filters
  const [analyticsFrom, setAnalyticsFrom] = useState(
    new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().slice(0, 10)
  );
  const [analyticsTo, setAnalyticsTo] = useState(new Date().toISOString().slice(0, 10));
  const [analyticsCategory, setAnalyticsCategory]   = useState("");
  const [analyticsDept, setAnalyticsDept]           = useState("");
  const [analyticsTeam, setAnalyticsTeam]           = useState("");
  const [analyticsAmountMin, setAnalyticsAmountMin] = useState("");
  const [analyticsAmountMax, setAnalyticsAmountMax] = useState("");
  const [analyticsPaidBy, setAnalyticsPaidBy]       = useState("");

  const { data: pendingExpenses = [], isLoading: pendingLoading } = useQuery({
    queryKey: ["expenses-pending"],
    queryFn: () => apiGetExpenses({ status: "pending_approval" }),
  });

  const { data: submittedExpenses = [] } = useQuery({
    queryKey: ["expenses-submitted"],
    queryFn: () => apiGetExpenses({ status: "submitted" }),
  });

  const pendingAll = [...submittedExpenses, ...pendingExpenses];

  const { data: allExpenses = [], isLoading: allLoading } = useQuery({
    queryKey: ["expenses-all", filterStatus, filterCategory, filterFrom, filterTo],
    queryFn: () => apiGetExpenses({
      status: filterStatus !== "all" ? filterStatus : undefined,
      category: filterCategory !== "all" ? filterCategory : undefined,
      from: filterFrom || undefined,
      to: filterTo || undefined,
    }),
    enabled: tab === "all",
  });

  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: apiGetDepartments,
    enabled: tab === "overview",
  });

  const { data: teamNames = [] } = useQuery({
    queryKey: ["team-names"],
    queryFn: apiGetTeamNames,
    enabled: tab === "overview",
  });

  const { data: analytics } = useQuery({
    queryKey: ["expense-analytics", analyticsFrom, analyticsTo, analyticsCategory, analyticsDept, analyticsTeam, analyticsAmountMin, analyticsAmountMax, analyticsPaidBy],
    queryFn: () => apiGetExpenseAnalytics({
      from: analyticsFrom,
      to: analyticsTo,
      ...(analyticsCategory  && { category:  analyticsCategory }),
      ...(analyticsDept      && { department: analyticsDept }),
      ...(analyticsTeam      && { team:       analyticsTeam }),
      ...(analyticsAmountMin && { amountMin:  analyticsAmountMin }),
      ...(analyticsAmountMax && { amountMax:  analyticsAmountMax }),
      ...(analyticsPaidBy    && { paidBy:     analyticsPaidBy }),
    }),
    enabled: tab === "overview",
  });

  const analyticsActiveFilters = [analyticsDept, analyticsTeam, analyticsCategory, analyticsPaidBy, analyticsAmountMin, analyticsAmountMax].filter(Boolean).length;

  const clearAnalyticsFilters = () => {
    setAnalyticsCategory("");
    setAnalyticsDept("");
    setAnalyticsTeam("");
    setAnalyticsAmountMin("");
    setAnalyticsAmountMax("");
    setAnalyticsPaidBy("");
  };

  const approveMutation = useMutation({
    mutationFn: (id: number) => apiApproveExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses-pending"] });
      queryClient.invalidateQueries({ queryKey: ["expenses-submitted"] });
      queryClient.invalidateQueries({ queryKey: ["expenses-all"] });
      toast({ title: t("expenses.toast.approved") });
    },
    onError: (err: any) => toast({ title: t("expenses.toast.error"), description: err.message, variant: "destructive" }),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => apiRejectExpense(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses-pending"] });
      queryClient.invalidateQueries({ queryKey: ["expenses-submitted"] });
      queryClient.invalidateQueries({ queryKey: ["expenses-all"] });
      setRejectDialog(null);
      setRejectReason("");
      toast({ title: t("expenses.toast.rejected") });
    },
    onError: (err: any) => toast({ title: t("expenses.toast.error"), description: err.message, variant: "destructive" }),
  });

  const reimburseMutation = useMutation({
    mutationFn: ({ id }: { id: number }) => apiReimburseExpense(id, {
      paymentDate,
      paymentMethod,
      notes: paymentNotes || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses-all"] });
      setReimburseDialog(null);
      toast({ title: t("expenses.toast.reimbursed") });
    },
    onError: (err: any) => toast({ title: t("expenses.toast.error"), description: err.message, variant: "destructive" }),
  });

  const openDetail = async (exp: Expense) => {
    try {
      const full = await apiGetExpense(exp.id);
      setSelectedExpense(full);
    } catch {
      setSelectedExpense(exp);
    }
    setDetailOpen(true);
  };

  // ── Cash advance queries ───────────────────────────────────────────────────
  const { data: cashAdvances = [], isLoading: caLoading } = useQuery({
    queryKey: ["cash-advances-all", caStatusFilter],
    queryFn: () => apiGetCashAdvances(caStatusFilter !== "all" ? { status: caStatusFilter } : undefined),
    enabled: tab === "advances",
  });

  // Pending cash advance counts for tab badge
  const { data: caPending = [] } = useQuery({
    queryKey: ["cash-advances-pending-counts"],
    queryFn: () => apiGetCashAdvances(),
  });
  const caPendingCount = caPending.filter((a) => {
    if (isHrAdmin) return ["pending_hr", "report_pending_hr"].includes(a.status);
    if (isLM) return ["pending_lm", "report_submitted"].includes(a.status);
    return false;
  }).length;

  const caApproveMutation = useMutation({
    mutationFn: async ({ adv, type }: { adv: CashAdvance; type: typeof caApproveType }) => {
      if (type === "lm") return apiApproveCashAdvanceLM(adv.id);
      if (type === "hr") return apiApproveCashAdvanceHR(adv.id);
      if (type === "report_lm") return apiApproveCashAdvanceReport(adv.id);
      return apiSettleCashAdvance(adv.id, { settlementNotes: caSettleNotes || undefined });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-advances-all"] });
      queryClient.invalidateQueries({ queryKey: ["cash-advances-pending-counts"] });
      setCaApproveDialog(null);
      setCaSettleNotes("");
      toast({ title: t("expenses.toast.actionCompleted") });
    },
    onError: (err: any) => toast({ title: t("expenses.toast.error"), description: err.message, variant: "destructive" }),
  });

  const caRejectMutation = useMutation({
    mutationFn: async ({ adv, type, reason }: { adv: CashAdvance; type: typeof caRejectType; reason: string }) => {
      if (type === "lm") return apiRejectCashAdvanceLM(adv.id, reason);
      if (type === "hr") return apiRejectCashAdvanceHR(adv.id, reason);
      return apiRejectCashAdvanceReport(adv.id, reason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-advances-all"] });
      queryClient.invalidateQueries({ queryKey: ["cash-advances-pending-counts"] });
      setCaRejectDialog(null);
      setCaRejectReason("");
      toast({ title: t("expenses.toast.caRejected") });
    },
    onError: (err: any) => toast({ title: t("expenses.toast.error"), description: err.message, variant: "destructive" }),
  });

  const openCaDetail = async (adv: CashAdvance) => {
    try {
      const full = await apiGetCashAdvance(adv.id);
      setCaDetailAdv(full);
    } catch {
      setCaDetailAdv(adv);
    }
    setCaDetailOpen(true);
  };

  // Auto-open expense detail when navigated from a notification (?expense=<id>)
  useEffect(() => {
    const expenseId = searchParams.get("expense");
    if (!expenseId) return;
    // Clear the param immediately so refresh doesn't re-open
    setSearchParams({}, { replace: true });
    apiGetExpense(Number(expenseId))
      .then((exp) => {
        setSelectedExpense(exp);
        setDetailOpen(true);
        setTab("pending");
      })
      .catch((err: any) => {
        const is403 = err?.status === 403 || err?.message?.includes("403");
        toast({
          title: is403 ? t("expenses.toast.accessDenied") : t("expenses.toast.notFound"),
          description: is403 ? t("expenses.toast.accessDeniedDesc") : t("expenses.toast.notFoundDesc"),
          variant: "destructive",
        });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = () => {
    let rows: (string | number)[][];
    let filename: string;

    if (tab === "overview") {
      if (!analytics) return;
      rows = [];
      rows.push(["Month", "Total", "Count"]);
      analytics.monthly.forEach((m) => rows.push([m.month, m.total, m.count]));
      rows.push([]);
      rows.push(["Category", "Total", "Count"]);
      analytics.byCategory.forEach((c) => rows.push([c.category, c.total, c.count]));
      rows.push([]);
      rows.push(["Department", "Total", "Count"]);
      analytics.byDepartment.forEach((d) => rows.push([d.department, d.total, d.count]));
      rows.push([]);
      rows.push(["Team", "Total", "Count"]);
      analytics.byTeam.forEach((t) => rows.push([t.teamName, t.total, t.count]));
      rows.push([]);
      rows.push(["Top Spender", "Total", "Count"]);
      analytics.topSpenders.forEach((s) => rows.push([s.employeeName, s.total, s.count]));
      rows.push([]);
      rows.push(["Pending Approval", analytics.pendingApproval.total, analytics.pendingApproval.count]);
      rows.push(["Reimbursement Backlog", analytics.reimbursementBacklog.total, analytics.reimbursementBacklog.count]);
      rows.push(["Total Tax", analytics.totalTax]);
      filename = `expense-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    } else {
      const source = tab === "all" ? allExpenses : pendingAll;
      rows = [
        ["ID", "Employee", "Title", "Category", "Amount", "Currency", "Date", "Status", "Submitted At"],
        ...source.map((e) => [
          e.id,
          e.employeeName ?? "",
          e.title,
          e.category,
          e.amount,
          e.currency,
          e.expenseDate,
          e.status,
          e.submittedAt ?? "",
        ]),
      ];
      filename = `expenses-${new Date().toISOString().slice(0, 10)}.csv`;
    }

    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
              <ReceiptText className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">
                {t("expenses.title")}
              </h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                {t("expenses.subtitle")}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="gap-2" onClick={exportCsv}>
            <Download className="h-4 w-4" />
            {t("expenses.actions.exportCsv")}
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger value="overview" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <TrendingUp className="h-3.5 w-3.5" />
              {t("expenses.tabs.overview")}
            </TabsTrigger>
            <TabsTrigger value="all" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Layers className="h-3.5 w-3.5" />
              {t("expenses.tabs.allExpenses")}
            </TabsTrigger>
            <TabsTrigger value="advances" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Wallet className="h-3.5 w-3.5" />
              {t("expenses.tabs.cashAdvances")}
              {caPendingCount > 0 && (
                <Badge variant="destructive" className="ml-1 h-4 px-1 text-[10px]">{caPendingCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Clock className="h-3.5 w-3.5" />
              {t("expenses.tabs.pending")}
              {pendingAll.length > 0 && (
                <Badge variant="destructive" className="h-4 min-w-4 px-1 text-[10px]">
                  {pendingAll.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── Pending tab ── */}
          <TabsContent value="pending" className="space-y-4 mt-4">
            {/* Pending expenses section */}
            {pendingLoading ? (
              <CardGridSkeleton count={3} />
            ) : pendingAll.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <ReceiptText className="h-3.5 w-3.5" />{t("expenses.labels.expenseClaims")}
                </p>
                {pendingAll.map((exp) => (
                  <Card
                    key={exp.id}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => openDetail(exp)}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{exp.title}</span>
                          <StatusBadge status={exp.status} />
                        </div>
                        <div className="text-sm text-muted-foreground mt-0.5 flex flex-wrap gap-3">
                          <span className="font-medium text-foreground">{exp.employeeName}</span>
                          <span>{t(`expenses.categories.${exp.category}`, exp.category)}</span>
                          <span>{format(new Date(exp.expenseDate), "dd MMM yyyy")}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-semibold text-lg">{exp.amount.toLocaleString()} {exp.currency}</p>
                      </div>
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-green-700 border-green-300 hover:bg-green-50 dark:hover:bg-green-950"
                          onClick={() => approveMutation.mutate(exp.id)}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {t("expenses.actions.approve")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10"
                          onClick={() => { setRejectDialog(exp); setRejectReason(""); }}
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          {t("expenses.actions.reject")}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pending cash advances section */}
            {(() => {
              const pendingCAs = caPending.filter((a) => {
                if (isHrAdmin) return ["pending_hr", "report_submitted", "report_pending_hr"].includes(a.status);
                if (isLM) return ["pending_lm", "report_submitted"].includes(a.status);
                return false;
              });
              if (pendingCAs.length === 0) return null;
              return (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <Wallet className="h-3.5 w-3.5" />{t("expenses.labels.cashAdvances")}
                  </p>
                  {pendingCAs.map((adv) => (
                    <Card
                      key={adv.id}
                      className="hover:shadow-sm transition-shadow cursor-pointer hover:border-border/80"
                      onClick={() => openCaDetail(adv)}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium truncate">{adv.purpose}</span>
                            <CaStatusBadge status={adv.status} />
                          </div>
                          <div className="text-sm text-muted-foreground mt-0.5 flex flex-wrap gap-3">
                            <span className="font-medium text-foreground">{adv.employeeName}</span>
                            <span>{t("expenses.labels.requestedOn", { date: format(new Date(adv.createdAt), "dd MMM yyyy") })}</span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold text-lg">{adv.amount.toLocaleString()} {adv.currency}</p>
                        </div>
                        <div className="flex gap-1 flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>
                          {adv.status === "pending_lm" && (isHrAdmin || isLM) && (
                            <>
                              <Button size="sm" variant="outline" className="gap-1 text-green-700 border-green-300 hover:bg-green-50 dark:hover:bg-green-950" onClick={() => { setCaApproveDialog(adv); setCaApproveType("lm"); }}>
                                <CheckCircle2 className="h-3.5 w-3.5" />{t("expenses.actions.approve")}
                              </Button>
                              <Button size="sm" variant="outline" className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => { setCaRejectDialog(adv); setCaRejectType("lm"); setCaRejectReason(""); }}>
                                <XCircle className="h-3.5 w-3.5" />{t("expenses.actions.reject")}
                              </Button>
                            </>
                          )}
                          {adv.status === "pending_hr" && isHrAdmin && (
                            <>
                              <Button size="sm" variant="outline" className="gap-1 text-green-700 border-green-300 hover:bg-green-50 dark:hover:bg-green-950" onClick={() => { setCaApproveDialog(adv); setCaApproveType("hr"); }}>
                                <CheckCircle2 className="h-3.5 w-3.5" />{t("expenses.actions.approve")}
                              </Button>
                              <Button size="sm" variant="outline" className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => { setCaRejectDialog(adv); setCaRejectType("hr"); setCaRejectReason(""); }}>
                                <XCircle className="h-3.5 w-3.5" />{t("expenses.actions.reject")}
                              </Button>
                            </>
                          )}
                          {adv.status === "report_submitted" && (isHrAdmin || isLM) && (
                            <>
                              <Button size="sm" variant="outline" className="gap-1 text-green-700 border-green-300 hover:bg-green-50 dark:hover:bg-green-950" onClick={() => { setCaApproveDialog(adv); setCaApproveType("report_lm"); }}>
                                <CheckCircle2 className="h-3.5 w-3.5" />{t("expenses.actions.approveReport")}
                              </Button>
                              <Button size="sm" variant="outline" className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => { setCaRejectDialog(adv); setCaRejectType("report_lm"); setCaRejectReason(""); }}>
                                <XCircle className="h-3.5 w-3.5" />{t("expenses.actions.rejectReport")}
                              </Button>
                            </>
                          )}
                          {adv.status === "report_pending_hr" && isHrAdmin && (
                            <Button size="sm" variant="outline" className="gap-1 text-green-700 border-green-300 hover:bg-green-50 dark:hover:bg-green-950" onClick={() => { setCaApproveDialog(adv); setCaApproveType("settle"); }}>
                              <Banknote className="h-3.5 w-3.5" />{t("expenses.actions.settle")}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}

            {/* Empty state — shown only when both lists are empty */}
            {pendingAll.length === 0 && caPendingCount === 0 && !pendingLoading && (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <CheckCircle2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>{t("expenses.empty.noPending")}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── All expenses tab ── */}
          <TabsContent value="all" className="space-y-4 mt-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <Label className="text-xs">{t("expenses.filters.status")}</Label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="mt-1 h-8 w-36 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("expenses.filters.allStatuses")}</SelectItem>
                        {Object.keys(STATUS_VARIANTS).map((v) => (
                          <SelectItem key={v} value={v}>{t(`expenses.status.${v === "pending_approval" ? "pending" : v}`, v)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{t("expenses.filters.category")}</Label>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="mt-1 h-8 w-44 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("expenses.filters.allCategories")}</SelectItem>
                        {["travel","food","office","accommodation","software","other"].map((c) => <SelectItem key={c} value={c}>{t(`expenses.categories.${c}`, c)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{t("expenses.filters.from")}</Label>
                    <DatePicker value={filterFrom} onChange={setFilterFrom} className="mt-1 h-8 w-36 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">{t("expenses.filters.to")}</Label>
                    <DatePicker value={filterTo} onChange={setFilterTo} className="mt-1 h-8 w-36 text-xs" />
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { setFilterStatus("all"); setFilterCategory("all"); setFilterFrom(""); setFilterTo(""); }}
                    className="h-8 text-xs gap-1"
                  >
                    <Filter className="h-3 w-3" />{t("expenses.filters.clear")}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {allLoading ? (
              <CardGridSkeleton count={6} />
            ) : allExpenses.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  {t("expenses.empty.noExpenses")}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {allExpenses.map((exp) => (
                  <Card
                    key={exp.id}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => openDetail(exp)}
                  >
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm truncate">{exp.title}</span>
                          <StatusBadge status={exp.status} />
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-2">
                          <span className="font-medium text-foreground">{exp.employeeName}</span>
                          <span>{t(`expenses.categories.${exp.category}`, exp.category)}</span>
                          <span>{format(new Date(exp.expenseDate), "dd MMM yyyy")}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-semibold">{exp.amount.toLocaleString()} {exp.currency}</p>
                      </div>
                      {exp.status === "approved" && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 h-7 text-xs text-blue-700 border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950"
                            onClick={() => {
                              setReimburseDialog(exp);
                              setPaymentDate(new Date().toISOString().slice(0, 10));
                              setPaymentMethod("bank_transfer");
                              setPaymentNotes("");
                            }}
                          >
                            <Banknote className="h-3 w-3" />
                            {t("expenses.actions.reimburse")}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── Overview tab ── */}
          <TabsContent value="overview" className="mt-4 space-y-5">

            {/* ── Filter strip ── */}
            <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-card px-4 py-3 shadow-sm overflow-x-auto">
              <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />

              {/* Date range */}
              <DatePicker value={analyticsFrom} onChange={setAnalyticsFrom} className="h-8 w-[140px] text-xs" />
              <span className="text-xs text-muted-foreground shrink-0">→</span>
              <DatePicker value={analyticsTo} onChange={setAnalyticsTo} className="h-8 w-[140px] text-xs" />

              <div className="h-5 w-px bg-border shrink-0" />

              {/* Department */}
              <Select value={analyticsDept || "_all"} onValueChange={(v) => setAnalyticsDept(v === "_all" ? "" : v)}>
                <SelectTrigger className={`h-8 text-sm w-[150px] ${analyticsDept ? "border-primary/60 text-primary" : ""}`}>
                  <SelectValue placeholder={t("profile.departmentLabel", "Department")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">{t("expenses.filters.allDepartments")}</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* Team */}
              <Select value={analyticsTeam || "_all"} onValueChange={(v) => setAnalyticsTeam(v === "_all" ? "" : v)}>
                <SelectTrigger className={`h-8 text-sm w-[130px] ${analyticsTeam ? "border-primary/60 text-primary" : ""}`}>
                  <SelectValue placeholder={t("profile.teamLabel", "Team")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">{t("expenses.filters.allTeams")}</SelectItem>
                  {teamNames.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* Category */}
              <Select value={analyticsCategory || "_all"} onValueChange={(v) => setAnalyticsCategory(v === "_all" ? "" : v)}>
                <SelectTrigger className={`h-8 text-sm w-[140px] ${analyticsCategory ? "border-primary/60 text-primary" : ""}`}>
                  <SelectValue placeholder={t("expenses.details.category", "Category")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">{t("expenses.filters.allCategories")}</SelectItem>
                  {["travel","food","office","accommodation","software","other"].map((c) => <SelectItem key={c} value={c}>{t(`expenses.categories.${c}`, c)}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* Paid By */}
              <Select value={analyticsPaidBy || "_all"} onValueChange={(v) => setAnalyticsPaidBy(v === "_all" ? "" : v)}>
                <SelectTrigger className={`h-8 text-sm w-[120px] ${analyticsPaidBy ? "border-primary/60 text-primary" : ""}`}>
                  <SelectValue placeholder={t("expenses.details.paidBy", "Paid By")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">{t("expenses.filters.anyPayer")}</SelectItem>
                  <SelectItem value="employee">{t("expenses.paidBy.employee")}</SelectItem>
                  <SelectItem value="company">{t("expenses.paidBy.company")}</SelectItem>
                </SelectContent>
              </Select>

              <div className="h-5 w-px bg-border shrink-0" />

              {/* Amount range */}
              <Input
                type="number" min={0} placeholder={t("expenses.filters.minAmount", "Min")}
                className={`h-8 text-sm w-[90px] ${analyticsAmountMin ? "border-primary/60" : ""}`}
                value={analyticsAmountMin}
                onChange={(e) => setAnalyticsAmountMin(e.target.value)}
              />
              <Input
                type="number" min={0} placeholder={t("expenses.filters.maxAmount", "Max")}
                className={`h-8 text-sm w-[90px] ${analyticsAmountMax ? "border-primary/60" : ""}`}
                value={analyticsAmountMax}
                onChange={(e) => setAnalyticsAmountMax(e.target.value)}
              />

              {/* Clear */}
              {analyticsActiveFilters > 0 && (
                <>
                  <div className="h-5 w-px bg-border shrink-0" />
                  <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-muted-foreground hover:text-foreground px-2" onClick={clearAnalyticsFilters}>
                    <X className="h-3 w-3" />
                    {t("expenses.filters.activeCount", { count: analyticsActiveFilters })}
                  </Button>
                </>
              )}
            </div>

            {analytics ? (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <SummaryCard icon={Clock} label={t("expenses.summary.pendingApproval")} value={analytics.pendingApproval.count} sub={`${analytics.pendingApproval.total.toLocaleString()} total`} color="text-amber-600" />
                  <SummaryCard icon={Banknote} label={t("expenses.summary.reimbursementBacklog")} value={analytics.reimbursementBacklog.count} sub={`${analytics.reimbursementBacklog.total.toLocaleString()} total`} color="text-blue-600" />
                  <SummaryCard icon={DollarSign} label={t("expenses.summary.totalTax")} value={analytics.totalTax.toLocaleString()} sub={t("expenses.summary.inPeriod")} color="text-purple-600" />
                  <SummaryCard icon={TrendingUp} label={t("expenses.summary.totalSpend")} value={analytics.monthly.reduce((s, m) => s + m.total, 0).toLocaleString()} sub={`${analytics.monthly.reduce((s, m) => s + m.count, 0)} ${t("expenses.summary.claims")}`} color="text-green-600" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Monthly bar chart */}
                  {analytics.monthly.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" />{t("expenses.charts.monthlySpend")}</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={analytics.monthly}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v: any) => v.toLocaleString()} />
                            <Bar dataKey="total" fill="#6366f1" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Category pie */}
                  {analytics.byCategory.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Layers className="h-4 w-4" />{t("expenses.charts.byCategory")}</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={analytics.byCategory}
                              dataKey="total"
                              nameKey="category"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label={({ category, percent }: any) => `${category} (${(percent * 100).toFixed(0)}%)`}
                              labelLine={false}
                            >
                              {analytics.byCategory.map((_, i) => (
                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v: any) => v.toLocaleString()} />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* By Department bar */}
                  {analytics.byDepartment.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Layers className="h-4 w-4" />{t("expenses.charts.byDepartment")}</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-2.5">
                          {analytics.byDepartment.map((d) => (
                            <div key={d.department} className="flex items-center gap-3">
                              <span className="text-sm w-36 truncate shrink-0">{d.department}</span>
                              <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                                <div
                                  className="h-2 rounded-full bg-primary"
                                  style={{ width: `${(d.total / analytics.byDepartment[0].total) * 100}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium w-24 text-right shrink-0">{d.total.toLocaleString()}</span>
                              <span className="text-xs text-muted-foreground w-10 text-right shrink-0">{d.count}×</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* By Team bar */}
                  {analytics.byTeam.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" />{t("expenses.charts.byTeam")}</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-2.5">
                          {analytics.byTeam.map((t) => (
                            <div key={t.teamName} className="flex items-center gap-3">
                              <span className="text-sm w-36 truncate shrink-0">{t.teamName}</span>
                              <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                                <div
                                  className="h-2 rounded-full bg-emerald-500"
                                  style={{ width: `${(t.total / analytics.byTeam[0].total) * 100}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium w-24 text-right shrink-0">{t.total.toLocaleString()}</span>
                              <span className="text-xs text-muted-foreground w-10 text-right shrink-0">{t.count}×</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* ── Cash Advance summary ── */}
                {caPending.length > 0 && (() => {
                  const caActive    = caPending.filter((a) => ["pending_lm", "pending_hr", "approved", "report_submitted", "report_pending_hr"].includes(a.status));
                  const caSettledList = caPending.filter((a) => a.status === "settled");
                  const caRejected  = caPending.filter((a) => ["rejected", "report_rejected"].includes(a.status));
                  const caTotalDisb = caPending.reduce((s, a) => s + a.amount, 0);
                  const caSettledTotal = caSettledList.reduce((s, a) => s + a.amount, 0);
                  const caMonthly: Record<string, number> = {};
                  caPending.forEach((a) => {
                    const m = a.createdAt.slice(0, 7);
                    caMonthly[m] = (caMonthly[m] ?? 0) + a.amount;
                  });
                  const caMonthlyData = Object.entries(caMonthly).sort(([a], [b]) => a.localeCompare(b)).map(([month, total]) => ({ month: month.slice(0, 7), total }));
                  const caByStatus = Object.entries(
                    caPending.reduce<Record<string, number>>((acc, a) => {
                      const label = Object.keys(CA_STATUS_COLORS).includes(a.status)
                        ? t(`expenses.caStatus.${{
                            pending_lm: "awaitingManager",
                            pending_hr: "awaitingHr",
                            approved: "awaitingReport",
                            report_submitted: "reportSubmitted",
                            report_pending_hr: "readyForSettlement",
                            report_rejected: "reportRejected",
                            settled: "settled",
                            rejected: "rejected",
                          }[a.status] ?? a.status}`, a.status)
                        : a.status;
                      acc[label] = (acc[label] ?? 0) + 1;
                      return acc;
                    }, {})
                  ).map(([name, value]) => ({ name, value }));

                  return (
                    <>
                      <div className="mt-2 border-t border-border/40 pt-4">
                        <p className="text-sm font-semibold flex items-center gap-2 mb-4">
                          <Wallet className="h-4 w-4 text-violet-500" />{t("expenses.charts.cashAdvancesOverview")}
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <SummaryCard icon={Wallet}   label={t("expenses.summary.totalIssued")}  value={caTotalDisb.toLocaleString()} sub={`${caPending.length} ${t("expenses.summary.advances")}`}     color="text-violet-600" />
                          <SummaryCard icon={Clock}    label={t("expenses.summary.inProgress")}  value={caActive.length}              sub={t("expenses.summary.awaitingAction")}                  color="text-amber-600" />
                          <SummaryCard icon={Banknote} label={t("expenses.summary.settled")}     value={caSettledTotal.toLocaleString()} sub={`${caSettledList.length} ${t("expenses.summary.advances")}`} color="text-green-600" />
                          <SummaryCard icon={XCircle}  label={t("expenses.summary.rejected")}    value={caRejected.length}            sub={t("expenses.summary.totalRejected")}                   color="text-red-500" />
                        </div>
                        {caMonthlyData.length > 0 && (
                          <Card className="mt-4">
                            <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" />{t("expenses.charts.monthlyCashAdvances")}</CardTitle></CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={caMonthlyData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                  <YAxis tick={{ fontSize: 11 }} />
                                  <Tooltip formatter={(v: any) => v.toLocaleString()} />
                                  <Bar dataKey="total" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        )}
                        {caByStatus.length > 0 && (
                          <Card className="mt-4">
                            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Layers className="h-4 w-4" />{t("expenses.charts.byStatus")}</CardTitle></CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                  <Pie data={caByStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, percent }: any) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                                    {caByStatus.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                  </Pie>
                                  <Tooltip />
                                </PieChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </>
                  );
                })()}

                {/* Top Spenders */}
                {analytics.topSpenders.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" />{t("expenses.charts.topSpenders")}</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-2.5">
                        {analytics.topSpenders.map((s) => (
                          <div key={s.employeeName} className="flex items-center gap-3">
                            <span className="text-sm w-44 truncate shrink-0 font-medium">{s.employeeName}</span>
                            <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                              <div
                                className="h-2 rounded-full bg-amber-500"
                                style={{ width: `${(s.total / analytics.topSpenders[0].total) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-24 text-right shrink-0">{s.total.toLocaleString()}</span>
                            <span className="text-xs text-muted-foreground w-10 text-right shrink-0">{s.count}×</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <div className="space-y-6">
                <StatCardsSkeleton count={4} />
                <CardGridSkeleton count={3} />
              </div>
            )}
          </TabsContent>
          {/* ── Cash Advances tab ── */}
          <TabsContent value="advances" className="space-y-4 mt-4">
            {/* Filter bar */}
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <Label className="text-xs">{t("expenses.filters.status")}</Label>
                <Select value={caStatusFilter} onValueChange={setCaStatusFilter}>
                  <SelectTrigger className="mt-1 h-8 w-44 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("expenses.filters.allStatuses")}</SelectItem>
                    {Object.keys(CA_STATUS_COLORS).map((v) => (
                      <SelectItem key={v} value={v}>{t(`expenses.caStatus.${{
                        pending_lm:"awaitingManager",pending_hr:"awaitingHr",approved:"awaitingReport",
                        report_submitted:"reportSubmitted",report_pending_hr:"readyForSettlement",
                        report_rejected:"reportRejected",settled:"settled",rejected:"rejected"
                      }[v]??v}`,v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="ghost" size="sm" className="h-8 text-xs gap-1"
                onClick={() => setCaStatusFilter("all")}
              >
                <Filter className="h-3 w-3" />{t("expenses.filters.clear")}
              </Button>
            </div>

            {caLoading ? (
              <CardGridSkeleton count={3} />
            ) : cashAdvances.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Wallet className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>{t("expenses.empty.noAdvances")}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {cashAdvances.map((adv) => {
                  const diff = adv.reportAmount !== null ? adv.reportAmount - adv.amount : null;
                  return (
                    <Card
                      key={adv.id}
                      className="hover:shadow-sm transition-shadow cursor-pointer hover:border-border/80"
                      onClick={() => openCaDetail(adv)}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium truncate">{adv.purpose}</span>
                            <CaStatusBadge status={adv.status} />
                          </div>
                          <div className="text-sm text-muted-foreground mt-0.5 flex flex-wrap gap-3">
                            <span className="font-medium text-foreground">{adv.employeeName}</span>
                            <span>{t("expenses.labels.requestedOn", { date: format(new Date(adv.createdAt), "dd MMM yyyy") })}</span>
                            {adv.reportAmount !== null && (
                              <span>
                                {t("expenses.modals.reportAmount")}: {adv.reportAmount.toLocaleString()} {adv.currency}
                                {diff !== null && diff !== 0 && (
                                  <span className="ml-1" style={{ color: diff > 0 ? "#3b82f6" : "#f59e0b" }}>
                                    ({diff > 0 ? "+" : ""}{diff.toFixed(2)})
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold text-lg">{adv.amount.toLocaleString()} {adv.currency}</p>
                        </div>
                        <div className="flex gap-1 flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>
                          {/* LM approve/reject initial request */}
                          {adv.status === "pending_lm" && (isHrAdmin || isLM) && (
                            <>
                              <Button
                                size="sm" variant="outline"
                                className="gap-1 text-green-700 border-green-300 hover:bg-green-50 dark:hover:bg-green-950"
                                onClick={() => { setCaApproveDialog(adv); setCaApproveType("lm"); }}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />{t("expenses.actions.approve")}
                              </Button>
                              <Button
                                size="sm" variant="outline"
                                className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10"
                                onClick={() => { setCaRejectDialog(adv); setCaRejectType("lm"); setCaRejectReason(""); }}
                              >
                                <XCircle className="h-3.5 w-3.5" />{t("expenses.actions.reject")}
                              </Button>
                            </>
                          )}
                          {/* HR approve/reject */}
                          {adv.status === "pending_hr" && isHrAdmin && (
                            <>
                              <Button
                                size="sm" variant="outline"
                                className="gap-1 text-green-700 border-green-300 hover:bg-green-50 dark:hover:bg-green-950"
                                onClick={() => { setCaApproveDialog(adv); setCaApproveType("hr"); }}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />{t("expenses.actions.approve")}
                              </Button>
                              <Button
                                size="sm" variant="outline"
                                className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10"
                                onClick={() => { setCaRejectDialog(adv); setCaRejectType("hr"); setCaRejectReason(""); }}
                              >
                                <XCircle className="h-3.5 w-3.5" />{t("expenses.actions.reject")}
                              </Button>
                            </>
                          )}
                          {/* LM approve/reject report */}
                          {adv.status === "report_submitted" && (isHrAdmin || isLM) && (
                            <>
                              <Button
                                size="sm" variant="outline"
                                className="gap-1 text-green-700 border-green-300 hover:bg-green-50 dark:hover:bg-green-950"
                                onClick={() => { setCaApproveDialog(adv); setCaApproveType("report_lm"); }}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />{t("expenses.actions.approveReport")}
                              </Button>
                              <Button
                                size="sm" variant="outline"
                                className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10"
                                onClick={() => { setCaRejectDialog(adv); setCaRejectType("report_lm"); setCaRejectReason(""); }}
                              >
                                <XCircle className="h-3.5 w-3.5" />{t("expenses.actions.rejectReport")}
                              </Button>
                            </>
                          )}
                          {/* HR settle */}
                          {adv.status === "report_pending_hr" && isHrAdmin && (
                            <Button
                              size="sm" variant="outline"
                              className="gap-1 text-purple-700 border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950"
                              onClick={() => { setCaApproveDialog(adv); setCaApproveType("settle"); setCaSettleNotes(""); }}
                            >
                              <Banknote className="h-3.5 w-3.5" />{t("expenses.actions.settle")}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* CA Approve / Settle dialog */}
        <Dialog open={!!caApproveDialog} onOpenChange={(v) => !v && setCaApproveDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {caApproveType === "settle" ? t("expenses.modals.settleCaTitle") :
                 caApproveType === "report_lm" ? t("expenses.modals.approveReportTitle") :
                 t("expenses.modals.approveCaTitle")}
              </DialogTitle>
              <DialogDescription>
                {caApproveDialog && (
                  <span>
                    <strong>{caApproveDialog.employeeName}</strong> — {caApproveDialog.amount.toLocaleString()} {caApproveDialog.currency}
                    {caApproveType === "settle" && caApproveDialog.reportAmount !== null && (
                      <span className="block mt-1">
                        {t("expenses.detail.reportAmount")}: {caApproveDialog.reportAmount.toLocaleString()} {caApproveDialog.currency}
                        {(() => {
                          const diff = caApproveDialog.reportAmount - caApproveDialog.amount;
                          if (diff === 0) return <span className="text-green-600"> {t("expenses.modals.exactMatch")}</span>;
                          if (diff > 0) return <span className="text-blue-600"> {t("expenses.modals.payout")} +{diff.toFixed(2)}</span>;
                          return <span className="text-amber-600"> {t("expenses.modals.deduction")} {diff.toFixed(2)}</span>;
                        })()}
                      </span>
                    )}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            {caApproveType === "settle" && (
              <FloatField
                label={t("expenses.modals.settleNotesLabel")}
                type="textarea"
                value={caSettleNotes}
                onChange={(v) => setCaSettleNotes(v as string)}
                placeholder={t("expenses.modals.settleNotesPlaceholder")}
              />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCaApproveDialog(null)}>{t("expenses.filters.clear") === "Clear" ? "Cancel" : t("common.cancel", "Cancel")}</Button>
              <Button
                disabled={caApproveMutation.isPending}
                onClick={() => caApproveDialog && caApproveMutation.mutate({ adv: caApproveDialog, type: caApproveType })}
                className="gap-2"
              >
                {caApproveType === "settle" ? <Banknote className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                {caApproveMutation.isPending ? t("expenses.modals.processing") :
                  caApproveType === "settle" ? t("expenses.modals.confirmSettlement") : t("expenses.modals.confirmApproval")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CA Reject dialog */}
        <Dialog open={!!caRejectDialog} onOpenChange={(v) => !v && setCaRejectDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {caRejectType === "report_lm" ? t("expenses.modals.rejectReportTitle") : t("expenses.modals.rejectCaTitle")}
              </DialogTitle>
              <DialogDescription>
                {caRejectDialog && `${caRejectDialog.employeeName} — ${caRejectDialog.amount.toLocaleString()} ${caRejectDialog.currency}`}
              </DialogDescription>
            </DialogHeader>
            <FloatField
              label={t("expenses.modals.rejectionReasonLabel")}
              required
              type="textarea"
              value={caRejectReason}
              onChange={(v) => setCaRejectReason(v as string)}
              placeholder={t("expenses.modals.rejectionReasonPlaceholder")}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setCaRejectDialog(null)}>{t("common.cancel", "Cancel")}</Button>
              <Button
                variant="destructive"
                disabled={!caRejectReason.trim() || caRejectMutation.isPending}
                onClick={() => caRejectDialog && caRejectMutation.mutate({ adv: caRejectDialog, type: caRejectType, reason: caRejectReason })}
              >
                {caRejectMutation.isPending ? t("expenses.modals.rejecting") : t("expenses.modals.confirmRejection")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CA Detail dialog */}
        {caDetailAdv && (
          <CaDetailDialog
            advance={caDetailAdv}
            open={caDetailOpen}
            onClose={() => { setCaDetailOpen(false); setCaDetailAdv(null); }}
            isHrAdmin={isHrAdmin}
            isLM={isLM}
            onApprove={(adv, type) => { setCaDetailOpen(false); setCaDetailAdv(null); setCaApproveDialog(adv); setCaApproveType(type); }}
            onReject={(adv, type) => { setCaDetailOpen(false); setCaDetailAdv(null); setCaRejectDialog(adv); setCaRejectType(type); setCaRejectReason(""); }}
          />
        )}

        {/* Detail dialog */}
        {selectedExpense && (
          <ExpenseDetailDialog
            expense={selectedExpense}
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
            onApprove={() => { approveMutation.mutate(selectedExpense.id); setDetailOpen(false); }}
            onReject={() => { setRejectDialog(selectedExpense); setDetailOpen(false); }}
          />
        )}

        {/* Reject dialog */}
        <Dialog open={!!rejectDialog} onOpenChange={(v) => !v && setRejectDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("expenses.modals.rejectExpenseTitle")}</DialogTitle>
              <DialogDescription>
                {t("expenses.modals.rejectExpenseDesc", { title: rejectDialog?.title ?? "" })}
              </DialogDescription>
            </DialogHeader>
            <FloatField
              label={t("expenses.modals.rejectionReasonLabel")}
              required
              type="textarea"
              value={rejectReason}
              onChange={(v) => setRejectReason(v as string)}
              placeholder={t("expenses.modals.rejectionReasonPlaceholder")}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialog(null)}>{t("common.cancel", "Cancel")}</Button>
              <Button
                variant="destructive"
                disabled={!rejectReason.trim() || rejectMutation.isPending}
                onClick={() => rejectMutation.mutate({ id: rejectDialog!.id, reason: rejectReason })}
              >
                {rejectMutation.isPending ? t("expenses.modals.rejecting") : t("expenses.modals.rejectExpenseTitle")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reimburse dialog */}
        <Dialog open={!!reimburseDialog} onOpenChange={(v) => !v && setReimburseDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("expenses.modals.reimburseTitle")}</DialogTitle>
              <DialogDescription>
                {t("expenses.modals.reimburseDesc", { title: reimburseDialog?.title ?? "" })}
              </DialogDescription>
            </DialogHeader>
            <div>
              <FloatWrapper label={t("expenses.modals.paymentDate")}>
                <DatePicker value={paymentDate} onChange={setPaymentDate} className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm" />
              </FloatWrapper>
              <FloatWrapper label={t("expenses.modals.paymentMethod")}>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">{t("expenses.modals.bankTransfer")}</SelectItem>
                    <SelectItem value="cash">{t("expenses.modals.cash")}</SelectItem>
                    <SelectItem value="other">{t("expenses.modals.other")}</SelectItem>
                  </SelectContent>
                </Select>
              </FloatWrapper>
              <FloatField
                label={t("expenses.modals.paymentNotes")}
                value={paymentNotes}
                onChange={(v) => setPaymentNotes(v as string)}
                placeholder={t("expenses.modals.transactionRefPlaceholder")}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReimburseDialog(null)}>{t("common.cancel", "Cancel")}</Button>
              <Button
                disabled={reimburseMutation.isPending}
                onClick={() => reimburseMutation.mutate({ id: reimburseDialog!.id })}
                className="gap-2"
              >
                <Banknote className="h-4 w-4" />
                {reimburseMutation.isPending ? t("expenses.modals.saving") : t("expenses.modals.markReimbursed")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function SummaryCard({ icon: Icon, label, value, sub, color }: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub: string;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="p-4 flex items-start gap-3">
        <div className={`mt-0.5 ${color}`}><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-xl font-semibold">{value}</p>
          <p className="text-xs text-muted-foreground">{sub}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Detail dialog ────────────────────────────────────────────────────────────

// ─── Expense detail sheet with vertical approval timeline ─────────────────────

function ExpenseDetailDialog({
  expense,
  open,
  onClose,
  onApprove,
  onReject,
}: {
  expense: Expense;
  open: boolean;
  onClose: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}) {
  const { t } = useTranslation();
  const isPending = ["submitted", "pending_approval"].includes(expense.status);
  const categoryLabel = CATEGORIES.find((c) => c.value === expense.category)?.label ?? expense.category;

  // Build timeline steps
  type TimelineStep = {
    id: string;
    label: string;
    sublabel?: string;
    date?: string | null;
    state: "done" | "active" | "pending" | "rejected";
    note?: string | null;
    icon: React.ElementType;
  };

  const steps: TimelineStep[] = [];

  // Step 1: Created / Submitted
  steps.push({
    id: "created",
    label: t("expenses.timeline.expenseSubmitted"),
    sublabel: expense.employeeName ?? undefined,
    date: expense.submittedAt ?? expense.createdAt,
    state: "done",
    icon: ReceiptText,
  });

  // Step 2: Under Review / Approval decision
  if (expense.status === "submitted" || expense.status === "pending_approval") {
    steps.push({
      id: "review",
      label: t("expenses.timeline.awaitingApproval"),
      sublabel: t("expenses.timeline.awaitingApprovalSub"),
      date: null,
      state: "active",
      icon: Clock,
    });
    steps.push({ id: "approved",  label: t("expenses.timeline.approved"),    state: "pending", icon: CheckCircle2 });
    steps.push({ id: "reimbursed", label: t("expenses.timeline.reimbursed"), state: "pending", icon: Banknote });
  } else if (expense.status === "approved") {
    steps.push({
      id: "approved",
      label: t("expenses.timeline.approved"),
      sublabel: expense.approvedByName || undefined,
      date: expense.approvedAt ?? expense.updatedAt,
      state: "done",
      icon: CheckCircle2,
    });
    steps.push({
      id: "reimbursed",
      label: t("expenses.timeline.awaitingReimbursement"),
      sublabel: t("expenses.timeline.awaitingReimbursementSub"),
      date: null,
      state: "active",
      icon: Banknote,
    });
  } else if (expense.status === "rejected") {
    steps.push({
      id: "rejected",
      label: t("expenses.timeline.rejected"),
      sublabel: expense.rejectedByName || undefined,
      date: expense.rejectedAt ?? expense.updatedAt,
      state: "rejected",
      note: expense.rejectionReason,
      icon: XCircle,
    });
  } else if (expense.status === "reimbursed") {
    steps.push({
      id: "approved",
      label: t("expenses.timeline.approved"),
      sublabel: expense.approvedByName || undefined,
      date: expense.approvedAt ?? null,
      state: "done",
      icon: CheckCircle2,
    });
    steps.push({
      id: "reimbursed",
      label: t("expenses.timeline.reimbursed"),
      sublabel: expense.payment?.reimbursedByName || undefined,
      date: expense.payment?.paymentDate ?? expense.updatedAt,
      state: "done",
      note: expense.payment
        ? `${expense.payment.paidAmount.toLocaleString()} ${expense.currency} via ${(expense.payment.paymentMethod ?? "bank_transfer").replace(/_/g, " ")}${expense.payment.notes ? ` — ${expense.payment.notes}` : ""}`
        : null,
      icon: Banknote,
    });
  }

  const dotClass = (state: TimelineStep["state"]) => {
    if (state === "done")     return "bg-green-500  border-green-500  text-white";
    if (state === "active")   return "bg-amber-500  border-amber-500  text-white";
    if (state === "rejected") return "bg-red-500    border-red-500    text-white";
    return "bg-muted border-border text-muted-foreground";
  };

  const lineClass = (state: TimelineStep["state"]) =>
    state === "done" ? "bg-green-400" : "bg-border";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ReceiptText className="h-5 w-5 text-amber-500" />
            {expense.title}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-foreground">{expense.employeeName}</span>
              <StatusBadge status={expense.status} />
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Summary card */}
        <div className="rounded-xl border border-border/60 bg-muted/30 p-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">{t("expenses.detail.amount")}</p>
            <p className="font-semibold text-base">{expense.amount.toLocaleString()} {expense.currency}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">{t("expenses.detail.category")}</p>
            <p className="font-medium">{t(`expenses.categories.${expense.category}`, categoryLabel)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">{t("expenses.detail.expenseDate")}</p>
            <p className="font-medium">{format(new Date(expense.expenseDate), "dd MMM yyyy")}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">{t("expenses.detail.paidBy")}</p>
            <p className="font-medium capitalize">{t(`expenses.paidBy.${expense.paidBy}`, expense.paidBy.replace(/_/g, " "))}</p>
          </div>
          {expense.taxAmount > 0 && (
            <div className="col-span-2">
              <p className="text-xs text-muted-foreground mb-0.5">{t("expenses.detail.tax")}</p>
              <p className="font-medium">{expense.taxAmount.toLocaleString()} {expense.currency}{expense.taxType ? ` (${expense.taxType})` : ""}</p>
            </div>
          )}
          {expense.description && (
            <div className="col-span-2">
              <p className="text-xs text-muted-foreground mb-0.5">{t("expenses.detail.description")}</p>
              <p className="text-foreground">{expense.description}</p>
            </div>
          )}
        </div>

        {/* Attachments */}
        {expense.attachments && expense.attachments.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("expenses.detail.receipts")}</p>
            <div className="space-y-1.5">
              {expense.attachments.map((att) => {
                const url = getExpenseReceiptUrl(expense.id, att.id);
                const isImage = att.mimeType?.startsWith("image/");
                return (
                  <div key={att.id} className="rounded-lg border border-border/60 bg-muted/20 p-2">
                    {isImage && (
                      <a href={url} target="_blank" rel="noreferrer" className="block mb-2">
                        <img
                          src={url}
                          alt={att.fileName}
                          className="max-h-48 w-full rounded-md object-contain bg-background"
                        />
                      </a>
                    )}
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      download={att.fileName}
                      className="flex items-center gap-2 text-primary text-sm hover:underline"
                    >
                      {isImage ? <Download className="h-4 w-4 shrink-0" /> : <FileText className="h-4 w-4 shrink-0" />}
                      <span className="truncate">{att.fileName}</span>
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Approval Timeline */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">{t("expenses.detail.approvalFlow")}</p>
          <div className="relative pl-6">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              const isLast = idx === steps.length - 1;
              return (
                <div key={step.id} className="relative flex gap-4 pb-5 last:pb-0">
                  {!isLast && (
                    <div className={`absolute left-[11px] top-6 bottom-0 w-0.5 ${lineClass(step.state)}`} />
                  )}
                  <div className={`relative z-10 flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center -ml-6 ${dotClass(step.state)}`}>
                    <Icon className="h-3 w-3" />
                  </div>
                  <div className="flex-1 min-w-0 -mt-0.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-medium leading-snug ${step.state === "pending" ? "text-muted-foreground" : "text-foreground"}`}>
                        {step.label}
                      </p>
                      {step.date && (
                        <p className="text-xs text-muted-foreground shrink-0 mt-0.5">
                          {format(new Date(step.date), "dd MMM yyyy")}
                        </p>
                      )}
                    </div>
                    {step.sublabel && (
                      <p className={`text-xs mt-0.5 ${step.state === "active" ? "text-amber-600 dark:text-amber-400 font-medium" : "text-muted-foreground"}`}>
                        {step.state === "active" && "⏳ "}{step.sublabel}
                      </p>
                    )}
                    {step.note && (
                      <div className={`mt-1.5 rounded-md px-3 py-2 text-xs ${step.state === "rejected" ? "bg-destructive/10 border border-destructive/20 text-destructive" : "bg-muted/60 text-muted-foreground"}`}>
                        {step.note}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.close", "Close")}</Button>
          {isPending && onReject && (
            <Button variant="destructive" className="gap-1" onClick={onReject}>
              <XCircle className="h-4 w-4" />{t("expenses.actions.reject")}
            </Button>
          )}
          {isPending && onApprove && (
            <Button className="gap-1" onClick={onApprove}>
              <CheckCircle2 className="h-4 w-4" />{t("expenses.actions.approve")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Cash Advance detail dialog ───────────────────────────────────────────────

function CaDetailDialog({
  advance,
  open,
  onClose,
  isHrAdmin = false,
  isLM = false,
  onApprove,
  onReject,
}: {
  advance: CashAdvance;
  open: boolean;
  onClose: () => void;
  isHrAdmin?: boolean;
  isLM?: boolean;
  onApprove?: (adv: CashAdvance, type: "lm" | "hr" | "report_lm" | "settle") => void;
  onReject?: (adv: CashAdvance, type: "lm" | "hr" | "report_lm") => void;
}) {
  const { t } = useTranslation();
  const diff = advance.reportAmount !== null ? advance.reportAmount - advance.amount : null;

  // ── Approval timeline ──────────────────────────────────────────────────────
  type TStep = { id: string; label: string; sub?: string; date?: string | null; state: "done" | "active" | "pending" | "rejected"; note?: string | null; icon: React.ElementType };
  const steps: TStep[] = [];

  steps.push({ id: "requested", label: t("expenses.timeline.requested"), sub: advance.employeeName ?? undefined, date: advance.createdAt, state: "done", icon: Wallet });

  if (advance.status === "pending_lm") {
    steps.push({ id: "lm", label: t("expenses.timeline.awaitingManagerApproval"), sub: t("expenses.timeline.awaitingManagerApprovalSub"), state: "active", icon: Clock });
  } else if (advance.status === "rejected" && !advance.lmApprovedAt) {
    steps.push({ id: "lm", label: t("expenses.timeline.rejectedByManager"), state: "rejected", note: advance.rejectionReason, icon: XCircle });
  } else if (advance.lmApprovedAt) {
    steps.push({ id: "lm", label: t("expenses.timeline.managerApproved"), date: advance.lmApprovedAt, state: "done", icon: CheckCircle2 });
  }

  if (advance.lmApprovedAt) {
    if (advance.status === "pending_hr") {
      steps.push({ id: "hr", label: t("expenses.timeline.awaitingHrApproval"), sub: t("expenses.timeline.awaitingHrApprovalSub"), state: "active", icon: Clock });
    } else if (advance.status === "rejected" && !advance.hrApprovedAt) {
      steps.push({ id: "hr", label: t("expenses.timeline.rejectedByHr"), state: "rejected", note: advance.rejectionReason, icon: XCircle });
    } else if (advance.hrApprovedAt) {
      steps.push({ id: "hr", label: t("expenses.timeline.hrApproved"), date: advance.hrApprovedAt, state: "done", icon: CheckCircle2 });
    }
  }

  if (advance.hrApprovedAt) {
    if (advance.status === "approved") {
      steps.push({ id: "report", label: t("expenses.timeline.awaitingExpenseReport"), sub: t("expenses.timeline.awaitingExpenseReportSub"), state: "active", icon: FileText });
    } else if (advance.reportSubmittedAt) {
      steps.push({ id: "report", label: t("expenses.timeline.reportSubmitted"), date: advance.reportSubmittedAt, state: "done", icon: FileText, sub: advance.reportAmount !== null ? `${advance.reportAmount.toLocaleString()} ${advance.currency}` : undefined });
    }
  }

  if (advance.reportSubmittedAt) {
    if (advance.status === "report_submitted") {
      steps.push({ id: "report_review", label: t("expenses.timeline.awaitingReportReview"), sub: t("expenses.timeline.awaitingReportReviewSub"), state: "active", icon: Clock });
    } else if (advance.status === "report_rejected") {
      steps.push({ id: "report_review", label: t("expenses.timeline.reportRejected"), state: "rejected", note: advance.rejectionReason, icon: XCircle });
    } else if (advance.reportLmApprovedAt) {
      steps.push({ id: "report_review", label: t("expenses.timeline.reportApprovedByManager"), date: advance.reportLmApprovedAt, state: "done", icon: CheckCircle2 });
    }
  }

  if (advance.reportLmApprovedAt || advance.status === "report_pending_hr") {
    if (advance.status === "report_pending_hr") {
      steps.push({ id: "settle", label: t("expenses.timeline.awaitingSettlement"), sub: t("expenses.timeline.awaitingSettlementSub"), state: "active", icon: Banknote });
    } else if (advance.status === "settled") {
      const settleNote = advance.settlementType === "none" ? t("expenses.modals.exactMatchFullySettled", "Exact match — fully settled") :
        advance.settlementType === "payout" ? `${t("expenses.modals.payout")}: ${advance.settlementAmount?.toLocaleString()} ${advance.currency}` :
        `${t("expenses.modals.salaryDeduction")}: ${advance.settlementAmount?.toLocaleString()} ${advance.currency}`;
      steps.push({ id: "settle", label: t("expenses.timeline.settled"), date: advance.settledAt ?? null, state: "done", note: settleNote + (advance.settlementNotes ? ` — ${advance.settlementNotes}` : ""), icon: Banknote });
    }
  }

  const dotClass = (state: TStep["state"]) => {
    if (state === "done")     return "bg-green-500 border-green-500 text-white";
    if (state === "active")   return "bg-amber-500 border-amber-500 text-white";
    if (state === "rejected") return "bg-red-500 border-red-500 text-white";
    return "bg-muted border-border text-muted-foreground";
  };
  const lineClass = (state: TStep["state"]) => state === "done" ? "bg-green-400" : "bg-border";

  // ── Action button logic ────────────────────────────────────────────────────
  const showApproveLM  = advance.status === "pending_lm"        && (isHrAdmin || isLM);
  const showApproveHR  = advance.status === "pending_hr"        && isHrAdmin;
  const showApproveRpt = advance.status === "report_submitted"  && (isHrAdmin || isLM);
  const showSettle     = advance.status === "report_pending_hr" && isHrAdmin;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            {t("expenses.caDetail.title")} #{advance.id} — {advance.employeeName}
          </DialogTitle>
          <DialogDescription>
            <CaStatusBadge status={advance.status} />
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          {/* Info grid */}
          <div className="rounded-xl border border-border/60 bg-muted/30 p-4 grid grid-cols-2 gap-x-6 gap-y-2">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">{t("expenses.caDetail.advanceAmount")}</p>
              <p className="font-semibold text-base">{advance.amount.toLocaleString()} {advance.currency}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">{t("expenses.caDetail.purpose")}</p>
              <p className="font-medium">{advance.purpose}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">{t("expenses.caDetail.requested")}</p>
              <p className="font-medium">{format(new Date(advance.createdAt), "dd MMM yyyy")}</p>
            </div>
            {advance.reportAmount !== null && (
              <>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">{t("expenses.caDetail.reportAmount")}</p>
                  <p className="font-semibold">
                    {advance.reportAmount.toLocaleString()} {advance.currency}
                    {diff !== null && diff !== 0 && (
                      <span className="ml-1 text-xs" style={{ color: diff > 0 ? "#3b82f6" : "#f59e0b" }}>
                        ({diff > 0 ? "+" : ""}{diff.toFixed(2)})
                      </span>
                    )}
                  </p>
                </div>
                {advance.reportNotes && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground mb-0.5">{t("expenses.caDetail.reportNotes")}</p>
                    <p>{advance.reportNotes}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Settlement info */}
          {advance.status === "settled" && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
              <p className="font-medium mb-1 flex items-center gap-1"><Banknote className="h-4 w-4" />{t("expenses.caDetail.settlement")}</p>
              {advance.settlementType === "none" && <p>{t("expenses.modals.exactMatchFullySettled", "Exact match — fully settled.")}</p>}
              {advance.settlementType === "payout" && <p className="text-green-700 dark:text-green-400">{t("expenses.modals.payout")}: {advance.settlementAmount?.toLocaleString()} {advance.currency}</p>}
              {advance.settlementType === "deduction" && <p className="text-amber-700 dark:text-amber-400">{t("expenses.modals.salaryDeduction")}: {advance.settlementAmount?.toLocaleString()} {advance.currency}</p>}
              {advance.settlementNotes && <p className="text-muted-foreground text-xs mt-1">{advance.settlementNotes}</p>}
            </div>
          )}

          {/* Receipts — images shown inline, PDFs as links */}
          {advance.receipts && advance.receipts.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("expenses.detail.receipts")}</p>
              <div className="space-y-2">
                {advance.receipts.map((att) => {
                  const url = getCashAdvanceReceiptUrl(advance.id, att.id);
                  const isImage = att.mimeType?.startsWith("image/");
                  return isImage ? (
                    <a key={att.id} href={url} target="_blank" rel="noreferrer" className="block">
                      <img src={url} alt={att.fileName} className="max-h-52 w-auto rounded-lg border border-border object-contain" />
                      <span className="text-xs text-muted-foreground mt-0.5 block">{att.fileName}</span>
                    </a>
                  ) : (
                    <a key={att.id} href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary text-sm hover:underline">
                      <FileText className="h-4 w-4 shrink-0" />{att.fileName}
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Approval timeline */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">{t("expenses.detail.approvalFlow")}</p>
            <div className="relative pl-6">
              {steps.map((step, idx) => {
                const Icon = step.icon;
                const isLast = idx === steps.length - 1;
                return (
                  <div key={step.id} className="relative flex gap-4 pb-5 last:pb-0">
                    {!isLast && <div className={`absolute left-[11px] top-6 bottom-0 w-0.5 ${lineClass(step.state)}`} />}
                    <div className={`relative z-10 flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center -ml-6 ${dotClass(step.state)}`}>
                      <Icon className="h-3 w-3" />
                    </div>
                    <div className="flex-1 min-w-0 -mt-0.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium leading-snug ${step.state === "pending" ? "text-muted-foreground" : "text-foreground"}`}>{step.label}</p>
                        {step.date && <p className="text-xs text-muted-foreground shrink-0 mt-0.5">{format(new Date(step.date), "dd MMM yyyy")}</p>}
                      </div>
                      {step.sub && (
                        <p className={`text-xs mt-0.5 ${step.state === "active" ? "text-amber-600 dark:text-amber-400 font-medium" : "text-muted-foreground"}`}>
                          {step.state === "active" && "⏳ "}{step.sub}
                        </p>
                      )}
                      {step.note && (
                        <div className={`mt-1.5 rounded-md px-3 py-2 text-xs ${step.state === "rejected" ? "bg-destructive/10 border border-destructive/20 text-destructive" : "bg-muted/60 text-muted-foreground"}`}>
                          {step.note}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>{t("common.close", "Close")}</Button>
          {(showApproveLM || showApproveHR || showApproveRpt) && onReject && (
            <Button
              variant="outline"
              className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10"
              onClick={() => onReject(advance, showApproveLM ? "lm" : showApproveHR ? "hr" : "report_lm")}
            >
              <XCircle className="h-4 w-4" />{showApproveRpt ? t("expenses.actions.rejectReport") : t("expenses.actions.reject")}
            </Button>
          )}
          {showApproveLM && onApprove && (
            <Button className="gap-1" onClick={() => onApprove(advance, "lm")}>
              <CheckCircle2 className="h-4 w-4" />{t("expenses.actions.approve")}
            </Button>
          )}
          {showApproveHR && onApprove && (
            <Button className="gap-1" onClick={() => onApprove(advance, "hr")}>
              <CheckCircle2 className="h-4 w-4" />{t("expenses.actions.approve")}
            </Button>
          )}
          {showApproveRpt && onApprove && (
            <Button className="gap-1" onClick={() => onApprove(advance, "report_lm")}>
              <CheckCircle2 className="h-4 w-4" />{t("expenses.actions.approveReport")}
            </Button>
          )}
          {showSettle && onApprove && (
            <Button className="gap-1 bg-purple-600 hover:bg-purple-700 text-white" onClick={() => onApprove(advance, "settle")}>
              <Banknote className="h-4 w-4" />{t("expenses.actions.settle", "Settle")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
